import React, { useState, useContext, useEffect } from 'react';
import styled, { css, RuleSet } from 'styled-components';
import { Warning } from '../../icons/Warning';
import { mediaQuery } from '../../theme';
import { AnimatePresence, motion } from 'framer-motion';
import { colors } from '../../theme/color';

export enum InfoBoxTypes {
  SUCCESS = 'success',
  WARNING = 'warning',
  ERROR = 'error',
  NOTIFICATION = 'notification',
  INFORMATION = 'information',
}

const handleColorType = (type: InfoBoxTypes): RuleSet<object> => {
  switch (type) {
    case InfoBoxTypes.SUCCESS:
      return css`
        background-color: ${colors.green.lighter(90)};
        color: ${colors.textDefault};

        svg {
          color: ${colors.green.base};
        }
      `;
    case InfoBoxTypes.WARNING:
      return css`
        background-color: ${colors.orange.lighter(90)};
        color: ${colors.orange.darker(20)};
        svg {
          color: ${colors.orange.base};
        }
      `;
    case InfoBoxTypes.ERROR:
      return css`
        background-color: ${colors.red.lighter(90)};
        color: ${colors.red.darker(20)};
        svg {
          color: ${colors.red.base};
        }
      `;
    case InfoBoxTypes.NOTIFICATION:
      return css`
        background-color: ${colors.orderSummaryAlert};
        color: ${colors.black};
        margin: 0px;
        svg {
          display: none;
        }
        a,
        a:hover {
          color: ${colors.black};
        }
      `;
    case InfoBoxTypes.INFORMATION:
      return css`
        background-color: #e7f5fb; // TODO: generate blue colors to theme
        color: ${colors.black};
        margin: 0px;
        svg {
          display: none;
        }
        a,
        a:hover {
          color: ${colors.black};
        }
      `;
  }
};

const InfoBoxContainer = styled.div<{ type: InfoBoxTypes }>`
  ${({ type }) => handleColorType(type)};
  width: 100%;
  border-radius: ${({ theme }) => theme.sizes?.borderRadius.default};
  display: flex;
  margin: 8px 0;
  @media ${mediaQuery.medium} {
    margin: 4px 0;
  }
`;

export const InfoBoxIconContainer = styled.div`
  margin: auto 0;
  padding: 8px;
`;

const InfoBoxText = styled.div`
  padding: 16px 16px 16px 4px;
  font-size: 1rem;
  display: flex;
  text-align: start;
  flex-direction: column;
`;

const StyledExpandInfoBoxClickable = styled.span`
  text-decoration: underline;
  color: ${colors.orange.base};
  &:hover {
    cursor: pointer;
    color: ${colors.orange.darker(60)};
  }
`;

type Context = {
  isExpanded: boolean;
  setExpanded: React.Dispatch<React.SetStateAction<boolean>>;
};

const initialContext = {
  isExpanded: false,
  setExpanded: () => undefined,
};

export const InfoBoxContext = React.createContext<Context>(initialContext);

export const InfoBox: React.FC<{
  type: InfoBoxTypes;
  className?: string;
  id?: string;
  children: React.ReactNode;
}> = ({ type, children, className, id }) => {
  const [isExpanded, setExpanded] = useState(false);

  useEffect(() => {
    return () => setExpanded(false);
  }, []);

  return (
    <InfoBoxContext.Provider value={{ isExpanded, setExpanded }}>
      <InfoBoxContainer type={type} className={className}>
        {type !== InfoBoxTypes.SUCCESS && (
          <InfoBoxIconContainer>
            <WarningIcon aria-hidden="true" />
          </InfoBoxIconContainer>
        )}
        <InfoBoxText id={id}>{children}</InfoBoxText>
      </InfoBoxContainer>
    </InfoBoxContext.Provider>
  );
};

export const ExpandInfoBoxClickable: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isExpanded, setExpanded } = useContext(InfoBoxContext);
  return (
    <StyledExpandInfoBoxClickable onClick={() => setExpanded(!isExpanded)}>
      {children}
    </StyledExpandInfoBoxClickable>
  );
};

export const ExpandContent: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isExpanded } = useContext(InfoBoxContext);
  return (
    <AnimatePresence>
      {isExpanded && (
        <motion.div
          key="expanded-info"
          initial={{ height: 0, opacity: 0 }}
          animate={{ height: 'auto', opacity: 1 }}
          transition={{ duration: 0.1 }}
          exit={{ height: 0, opacity: 0 }}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const WarningIcon = styled(Warning)`
  width: 30px;
  height: 30px;
`;
